import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { CustomerGuard } from '@next/core-lib/relationships';
import { RouterSelectors } from '@next/core-lib/router-store';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { SiteState } from 'src/app/shared/models';

import { selectSessionCustomerId } from 'src/app/store/selectors';

import * as formActions from '../store/actions/registration.actions';
import {
  selectGrainCertificates,
  selectCultivations,
  selectCustomer,
} from '../store/selectors/registration.selectors';

@Injectable({
  providedIn: 'root',
})
export class RegistrationGuard {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly customerGuard = inject(CustomerGuard);

  getData() {
    return this.store.pipe(
      select(RouterSelectors.getMergedRoute),
      withLatestFrom(
        this.store.select(selectGrainCertificates),
        this.store.select(selectCultivations),
        this.store.select(selectCustomer),
        this.store.select(selectSessionCustomerId),
      ),
      take(1),
      tap(([route, certificates, cultivations, customer, sessionCustomerId]) => {
        if (route.url === '/registration' || !certificates) {
          this.store.dispatch(formActions.getAllGrainCertificates());
        }
        if (route.url === '/registration' || !cultivations) {
          this.store.dispatch(formActions.getAllGrainCultivations());
        }
        if (
          route.url === '/registration' ||
          !customer ||
          (customer && customer.id !== sessionCustomerId)
        ) {
          this.store.dispatch(formActions.getCustomerWithGrower());
        }
      }),
    );
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.customerGuard.canActivate(route, state).pipe(
      switchMap((result) => {
        if (typeof result !== 'boolean') {
          return of(result);
        }

        return this.getData().pipe(
          switchMap(() => [true]),
          take(1),
        );
      }),
    );
  }
}
